import { m } from 'framer-motion';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { varFade, MotionViewport } from 'src/components/animate';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { SupportEmailRequest } from 'src/models/common';
import { sendSupportEmail } from 'src/api/common';
import LoadingButton from '@mui/lab/LoadingButton';
import { usePopup } from 'src/hooks/use-popup';

// ----------------------------------------------------------------------

const SupportEmailSendingSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().required('Email is required'),
  subject: Yup.string().required('Subject is required'),
  message: Yup.string().required('Message is required'),
});

export default function FaqsForm() {
  const methods = useForm({
    resolver: yupResolver(SupportEmailSendingSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const { setError, setSuccess } = usePopup();

  const onSubmit = handleSubmit(async (data: SupportEmailRequest) => {
    try {
      await sendSupportEmail(data);

      showSuccessPopup('Email sent successfully', 'Success');
      reset();
    } catch (errorL) {
      showErrorPopup('Failed to sent email', 'Error');
      reset();
    }
  });

  const showSuccessPopup = useCallback(
    (message: string, title: string) => {
      setSuccess(message, {
        title,
        actionText: 'OK',
      });
    },
    [setSuccess]
  );

  const showErrorPopup = useCallback(
    (message: string, title: string) => {
      setError(message, {
        title,
        actionText: 'ERROR',
      });
    },
    [setError]
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack component={MotionViewport} spacing={3}>
        <m.div variants={varFade().inUp}>
          <Typography variant="h4">{`Haven't found the right help?`}</Typography>
        </m.div>

        <m.div variants={varFade().inUp}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => <RHFTextField label="Name" {...field} />}
          />
        </m.div>

        <m.div variants={varFade().inUp}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => <RHFTextField label="Email" {...field} />}
          />
        </m.div>

        <m.div variants={varFade().inUp}>
          <Controller
            name="subject"
            control={control}
            render={({ field }) => <RHFTextField label="Subject" {...field} />}
          />
        </m.div>

        <m.div variants={varFade().inUp}>
          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <RHFTextField label="Enter your message here" {...field} multiline rows={4} />
            )}
          />{' '}
        </m.div>

        <m.div variants={varFade().inUp}>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Submit Now
          </LoadingButton>
        </m.div>
      </Stack>
    </FormProvider>
  );
}
